import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import logoImage from '../../assets/images/logo.webp';
import { cn } from '@/lib/utils';
import { MobileMenuButton, MobileNav } from './MobileNav';
import { EventsNavBadge } from './EventsNavBadge';

interface PublicHeaderProps {
  isScrolled: boolean;
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (open: boolean) => void;
  navLinks: { href: string; label: string }[];
  isActive: (path: string) => boolean;
}

export function PublicHeader({
  isScrolled,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  navLinks,
  isActive,
}: PublicHeaderProps) {
  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="sticky top-0 z-50 px-4 sm:px-6 lg:px-8 py-4 print:hidden"
    >
      <div className={cn(
        "absolute top-0 left-1/2 -translate-x-1/2 h-4 max-w-7xl w-full backdrop-blur-xl transition-opacity duration-300",
        isScrolled ? "opacity-100" : "opacity-0"
      )} />
      <nav className={cn(
        'max-w-7xl mx-auto px-5 sm:px-7 lg:px-9 transition-all duration-700 ease-out relative',
        isScrolled
          ? 'bg-white/90 backdrop-blur-2xl shadow-2xl shadow-slate-900/10 rounded-3xl border border-slate-200/60'
          : 'bg-white/60 backdrop-blur-md rounded-3xl border border-slate-100/40'
      )}>
        <div className={cn(
          "absolute top-0 left-1/2 -translate-x-1/2 h-[2px] transition-all duration-700",
          isScrolled ? "w-3/4 bg-gradient-to-r from-transparent via-[#1E40AF]/40 to-transparent" : "w-0"
        )} />

        <div className="flex items-center justify-between h-16 lg:h-20">
          <HeaderLogo />
          <DesktopNav navLinks={navLinks} isActive={isActive} />
          <DesktopCTA />

          <div className="flex items-center gap-2 lg:hidden">
            <LanguageSwitcher variant="minimal" />
            <MobileMenuButton
              isMobileMenuOpen={isMobileMenuOpen}
              setIsMobileMenuOpen={setIsMobileMenuOpen}
            />
          </div>
        </div>

        <MobileNav
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          navLinks={navLinks}
          isActive={isActive}
        />
      </nav>
    </motion.header>
  );
}

export function HeaderLogo() {
  return (
    <Link to="/" className="flex items-center gap-3 group relative z-10">
      <motion.div
        whileHover={{ scale: 1.08, rotate: 5 }}
        transition={{ type: 'spring', stiffness: 500, damping: 20 }}
        className="relative"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E40AF]/30 to-[#FBBF24]/30 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#FBBF24]/20 to-[#1E40AF]/20 rounded-2xl blur-2xl opacity-0 group-hover:opacity-70 transition-opacity duration-700" />
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-blue-400/10 to-amber-400/10 rounded-2xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0, 0.3, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <img
          src={logoImage}
          alt="Logo de Youth Impact Mission Burundi"
          width={48}
          height={48}
          loading="eager"
          decoding="async"
          className="h-11 w-auto sm:h-12 relative z-10 drop-shadow-sm"
        />
      </motion.div>
      <div className="flex flex-col">
        <motion.span
          className="font-bold text-slate-900 text-xl tracking-tight group-hover:text-[#1E40AF] transition-colors duration-300"
          whileHover={{ letterSpacing: "0.05em" }}
          transition={{ duration: 0.3 }}
        >
          YIM
        </motion.span>
        <span className="text-[10px] text-slate-700 font-semibold tracking-[0.2em] uppercase -mt-1 group-hover:text-[#FBBF24] transition-colors duration-300">
          Burundi
        </span>
      </div>
    </Link>
  );
}

export function DesktopNav({
  navLinks,
  isActive,
}: {
  navLinks: { href: string; label: string }[];
  isActive: (path: string) => boolean;
}) {
  return (
    <div className="hidden lg:flex items-center gap-1.5 bg-slate-50/50 rounded-full p-1.5 border border-slate-200/50">
      {navLinks.map((link) => (
        <Link
          key={link.href}
          to={link.href}
          className={cn(
            'relative px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 group',
            isActive(link.href)
              ? 'text-white'
              : 'text-slate-600 hover:text-[#1E40AF]'
          )}
        >
          <span className="relative z-10 flex items-center gap-2">
            {link.label}
            {link.href === '/events' && (
              <EventsNavBadge active={isActive(link.href)} />
            )}
          </span>
          {isActive(link.href) && (
            <>
              <motion.div
                layoutId="activeNav"
                className="absolute inset-0 bg-gradient-to-br from-[#1E40AF] via-[#2563eb] to-[#3b82f6] rounded-full -z-0 shadow-lg shadow-blue-500/30"
                transition={{ type: 'spring', stiffness: 400, damping: 35 }}
              />
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-transparent rounded-full"
                animate={{
                  scale: [1, 1.05, 1],
                  opacity: [0.5, 0.8, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </>
          )}
          {!isActive(link.href) && (
            <motion.div
              className="absolute inset-0 bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              initial={false}
            />
          )}
        </Link>
      ))}
    </div>
  );
}

function DesktopCTA() {
  const { t } = useTranslation();

  return (
    <div className="hidden lg:flex items-center gap-3">
      <LanguageSwitcher variant="minimal" />

      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.96 }}
        transition={{ type: 'spring', stiffness: 500, damping: 20 }}
      >
        <Link
          to="/get-involved"
          className="relative group px-6 py-3 text-sm font-bold inline-flex items-center justify-center rounded-full border-2 border-[#1E40AF] text-[#1E40AF] hover:text-white overflow-hidden transition-colors duration-500"
        >
          <div className="absolute inset-0 translate-y-[85%] group-hover:translate-y-0 transition-transform duration-700 ease-out">
            <div className="absolute inset-0 bg-gradient-to-t from-[#1E40AF] via-[#2563eb] to-[#3b82f6]" />
            <svg
              className="absolute -top-2 h-4 animate-wave w-[150%] left-[-25%]"
              viewBox="0 0 120 16"
              preserveAspectRatio="none"
            >
              <path
                d="M0,8 C20,3 40,13 60,8 C80,3 100,13 120,8 L120,16 L0,16 Z"
                fill="#3b82f6"
              />
            </svg>
          </div>
          <span className="relative z-10">
            {t('nav.getInvolved')}
          </span>
        </Link>
      </motion.div>

      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.96 }}
        transition={{ type: 'spring', stiffness: 500, damping: 20 }}
        className="relative"
      >
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-[#FBBF24] to-[#f59e0b] rounded-full blur-lg opacity-40"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.4, 0.6, 0.4],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <Link
          to="/donate"
          className="group relative flex items-center gap-2 px-6 py-3 text-sm font-bold bg-gradient-to-r from-[#FBBF24] via-[#f59e0b] to-[#FBBF24] bg-size-200 bg-pos-0 hover:bg-pos-100 text-white rounded-full shadow-md shadow-amber-500/20 hover:shadow-lg hover:shadow-amber-500/30 transition-all duration-500"
          style={{
            backgroundSize: '200% 100%',
          }}
        >
          <Heart className="w-4 h-4 group-hover:scale-110 group-hover:rotate-12 transition-transform duration-300" />
          <span>{t('nav.donate')}</span>
          <motion.div
            className="absolute inset-0 rounded-full"
            animate={{
              boxShadow: [
                '0 0 0 0 rgba(251, 191, 36, 0.4)',
                '0 0 0 8px rgba(251, 191, 36, 0)',
              ],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeOut"
            }}
          />
        </Link>
      </motion.div>
    </div>
  );
}
